import React, { useEffect, useState } from "react";
import axios from "axios";

const ENV_API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/online-yoga`;

const defaultClass = () => ({
  title: "",
  time: "",
  price: "",
  image: "",
});

const defaultBenefit = () => ({
  text: "",
});

const EMPTY_FORM = {
  heroEyebrow: "Transform Your Life",
  heroTitle: "Best Online Yoga Classes",
  heroSubtitle:
    "Yoga is more than a physical practice. It is a deep discipline that can dramatically improve the quality of life.",
  heroDescription:
    "If you have a casual interest in yoga or want to move toward teaching, this online journey gives you a grounded place to begin.",
  heroButtonText: "Start Your Journey",
  heroButtonLink: "/BookingForm",
  heroBackgroundImage: "",
  classSectionHeading: "Choose Your Class and Slot",
  classes: [defaultClass()],
  benefitsImage: "",
  benefitsHeading: "Benefits Of Yoga from Hatha Yogashram",
  benefits: [defaultBenefit()],
  finalCtaTitle:
    "Embark Your Yogic Journey with Yoga Alliance Certified Yoga Teacher Training School in Rishikesh, India",
  finalCtaButtonText: "Book Now",
  finalCtaButtonLink: "/BookingForm",
};

const resolveImageSrc = (image) => {
  if (!image) return "";
  if (image instanceof File) {
    return URL.createObjectURL(image);
  }
  if (/^https?:\/\//.test(image)) return image;
  if (image.startsWith("/uploads/")) return `${ENV_API_BASE_URL}${image}`;
  return `${ENV_API_BASE_URL}/uploads/${image}`;
};

const normalizeItem = (item) => ({
  ...EMPTY_FORM,
  ...item,
  classes: Array.isArray(item?.classes) && item.classes.length
    ? item.classes.map((course) => ({
        title: course?.title || "",
        time: course?.time || "",
        price: course?.price || "",
        image: course?.image || "",
      }))
    : [defaultClass()],
  benefits: Array.isArray(item?.benefits) && item.benefits.length
    ? item.benefits.map((benefit) => ({
        text: benefit?.text || "",
      }))
    : [defaultBenefit()],
});

function OnlineYogaForm() {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API);
      setRecords(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      console.error("FETCH ERROR:", error.response?.data || error.message);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditId(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleClassChange = (index, e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => {
      const nextClasses = [...prev.classes];
      nextClasses[index] = {
        ...nextClasses[index],
        [name]: files ? files[0] : value,
      };

      return {
        ...prev,
        classes: nextClasses,
      };
    });
  };

  const handleBenefitChange = (index, value) => {
    setFormData((prev) => {
      const nextBenefits = [...prev.benefits];
      nextBenefits[index] = { text: value };

      return {
        ...prev,
        benefits: nextBenefits,
      };
    });
  };

  const addClass = () => {
    setFormData((prev) => ({
      ...prev,
      classes: [...prev.classes, defaultClass()],
    }));
  };

  const removeClass = (index) => {
    setFormData((prev) => ({
      ...prev,
      classes:
        prev.classes.length > 1
          ? prev.classes.filter((_, currentIndex) => currentIndex !== index)
          : [defaultClass()],
    }));
  };

  const addBenefit = () => {
    setFormData((prev) => ({
      ...prev,
      benefits: [...prev.benefits, defaultBenefit()],
    }));
  };

  const removeBenefit = (index) => {
    setFormData((prev) => ({
      ...prev,
      benefits:
        prev.benefits.length > 1
          ? prev.benefits.filter((_, currentIndex) => currentIndex !== index)
          : [defaultBenefit()],
    }));
  };

  const buildPayload = () => {
    const payload = new FormData();

    payload.append("heroEyebrow", formData.heroEyebrow || "");
    payload.append("heroTitle", formData.heroTitle || "");
    payload.append("heroSubtitle", formData.heroSubtitle || "");
    payload.append("heroDescription", formData.heroDescription || "");
    payload.append("heroButtonText", formData.heroButtonText || "");
    payload.append("heroButtonLink", formData.heroButtonLink || "");
    payload.append("classSectionHeading", formData.classSectionHeading || "");
    payload.append("benefitsHeading", formData.benefitsHeading || "");
    payload.append("finalCtaTitle", formData.finalCtaTitle || "");
    payload.append("finalCtaButtonText", formData.finalCtaButtonText || "");
    payload.append("finalCtaButtonLink", formData.finalCtaButtonLink || "");

    if (typeof formData.heroBackgroundImage === "string") {
      payload.append("heroBackgroundImage", formData.heroBackgroundImage);
    } else if (formData.heroBackgroundImage instanceof File) {
      payload.append("heroBackgroundImage", formData.heroBackgroundImage);
    }

    if (typeof formData.benefitsImage === "string") {
      payload.append("benefitsImage", formData.benefitsImage);
    } else if (formData.benefitsImage instanceof File) {
      payload.append("benefitsImage", formData.benefitsImage);
    }

    const classes = formData.classes.map((course) => ({
      title: course.title || "",
      time: course.time || "",
      price: course.price || "",
      image: typeof course.image === "string" ? course.image : "",
    }));

    payload.append("classes", JSON.stringify(classes));

    formData.classes.forEach((course, index) => {
      if (course.image instanceof File) {
        payload.append(`classes[${index}][image]`, course.image);
      }
    });

    payload.append(
      "benefits",
      JSON.stringify(
        formData.benefits.map((benefit) => ({
          text: benefit.text || "",
        }))
      )
    );

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      const payload = buildPayload();

      if (editId) {
        await axios.put(`${API}/${editId}`, payload);
      } else {
        await axios.post(API, payload);
      }

      await fetchData();
      resetForm();
    } catch (error) {
      console.error("SAVE ERROR:", error.response?.data || error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setFormData(normalizeItem(item));
    setEditId(item._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;

    try {
      await axios.delete(`${API}/${id}`);
      await fetchData();
    } catch (error) {
      console.error("DELETE ERROR:", error.response?.data || error.message);
    }
  };

  const handleCreate = () => {
    setFormData(EMPTY_FORM);
    setEditId(null);
    setShowForm(true);
  };

  return (
    <div className="container pt-5 mt-5">
      {!showForm && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3 className="mb-0">Online Yoga CMS</h3>
            <button className="btn btn-primary" onClick={handleCreate}>
              + Create
            </button>
          </div>

          <table className="table table-bordered align-middle">
            <thead className="table-dark">
              <tr>
                <th>S.No</th>
                <th>Hero</th>
                <th>Classes</th>
                <th>Benefits</th>
                <th>CTA</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && records.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">
                    No online yoga content found.
                  </td>
                </tr>
              )}

              {records.map((item, index) => (
                <tr key={item._id}>
                  <td>{index + 1}</td>
                  <td>
                    <div className="fw-bold">{item.heroTitle}</div>
                    <div className="small text-muted">{item.heroEyebrow}</div>
                    {item.heroBackgroundImage && (
                      <img
                        src={resolveImageSrc(item.heroBackgroundImage)}
                        alt={item.heroTitle}
                        style={{
                          width: "120px",
                          height: "70px",
                          objectFit: "cover",
                          marginTop: "8px",
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <div className="fw-bold">{item.classSectionHeading}</div>
                    <div className="small text-muted">
                      {item.classes?.length || 0} classes
                    </div>
                  </td>
                  <td>
                    <div className="fw-bold">{item.benefitsHeading}</div>
                    <div className="small text-muted">
                      {item.benefits?.length || 0} benefits
                    </div>
                  </td>
                  <td>
                    <div className="fw-bold">{item.finalCtaTitle}</div>
                    <div className="small text-muted">
                      {item.finalCtaButtonText}
                    </div>
                  </td>
                  <td>
                    <button
                      className="btn btn-warning btn-sm me-2"
                      onClick={() => handleEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="card p-4 shadow">
          <h4 className="mb-3">Hero Section</h4>

          <label>Eyebrow</label>
          <input
            name="heroEyebrow"
            className="form-control mb-2"
            value={formData.heroEyebrow}
            onChange={handleChange}
          />

          <label>Hero Title</label>
          <input
            name="heroTitle"
            className="form-control mb-2"
            value={formData.heroTitle}
            onChange={handleChange}
            required
          />

          <label>Hero Subtitle</label>
          <textarea
            name="heroSubtitle"
            className="form-control mb-2"
            value={formData.heroSubtitle}
            onChange={handleChange}
            rows="3"
          />

          <label>Hero Description</label>
          <textarea
            name="heroDescription"
            className="form-control mb-2"
            value={formData.heroDescription}
            onChange={handleChange}
            rows="3"
          />

          <label>Button Text</label>
          <input
            name="heroButtonText"
            className="form-control mb-2"
            value={formData.heroButtonText}
            onChange={handleChange}
          />

          <label>Button Link</label>
          <input
            name="heroButtonLink"
            className="form-control mb-3"
            value={formData.heroButtonLink}
            onChange={handleChange}
          />

          <label>Hero Background Image</label>
          <input
            name="heroBackgroundImage"
            type="file"
            className="form-control mb-2"
            onChange={handleChange}
          />
          {formData.heroBackgroundImage && (
            <img
              src={resolveImageSrc(formData.heroBackgroundImage)}
              alt="Hero preview"
              className="mb-4"
              style={{ width: "220px", height: "120px", objectFit: "cover" }}
            />
          )}

          <h4 className="mt-4 mb-3">Classes Section</h4>

          <label>Class Section Heading</label>
          <input
            name="classSectionHeading"
            className="form-control mb-3"
            value={formData.classSectionHeading}
            onChange={handleChange}
          />

          {formData.classes.map((course, index) => (
            <div key={index} className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Class {index + 1}</h6>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeClass(index)}
                >
                  Remove
                </button>
              </div>

              <label>Title</label>
              <input
                name="title"
                className="form-control mb-2"
                value={course.title}
                onChange={(e) => handleClassChange(index, e)}
              />

              <label>Time</label>
              <input
                name="time"
                className="form-control mb-2"
                value={course.time}
                onChange={(e) => handleClassChange(index, e)}
              />

              <label>Price</label>
              <input
                name="price"
                className="form-control mb-2"
                value={course.price}
                onChange={(e) => handleClassChange(index, e)}
              />

              <label>Image</label>
              <input
                name="image"
                type="file"
                className="form-control mb-2"
                onChange={(e) => handleClassChange(index, e)}
              />
              {course.image && (
                <img
                  src={resolveImageSrc(course.image)}
                  alt={course.title || `Class ${index + 1}`}
                  style={{ width: "160px", height: "100px", objectFit: "cover" }}
                />
              )}
            </div>
          ))}

          <button
            type="button"
            className="btn btn-primary mb-3"
            onClick={addClass}
          >
            + Add Class
          </button>

          <h4 className="mt-4 mb-3">Benefits Section</h4>

          <label>Benefits Image</label>
          <input
            name="benefitsImage"
            type="file"
            className="form-control mb-2"
            onChange={handleChange}
          />
          {formData.benefitsImage && (
            <img
              src={resolveImageSrc(formData.benefitsImage)}
              alt="Benefits preview"
              className="mb-3"
              style={{ width: "220px", height: "120px", objectFit: "cover" }}
            />
          )}

          <label>Benefits Heading</label>
          <input
            name="benefitsHeading"
            className="form-control mb-3"
            value={formData.benefitsHeading}
            onChange={handleChange}
          />

          {formData.benefits.map((benefit, index) => (
            <div key={index} className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Benefit {index + 1}</h6>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeBenefit(index)}
                >
                  Remove
                </button>
              </div>

              <label>Text</label>
              <input
                className="form-control"
                value={benefit.text}
                onChange={(e) => handleBenefitChange(index, e.target.value)}
              />
            </div>
          ))}

          <button
            type="button"
            className="btn btn-primary mb-3"
            onClick={addBenefit}
          >
            + Add Benefit
          </button>

          <h4 className="mt-4 mb-3">Final CTA Section</h4>

          <label>CTA Title</label>
          <textarea
            name="finalCtaTitle"
            className="form-control mb-2"
            value={formData.finalCtaTitle}
            onChange={handleChange}
            rows="3"
          />

          <label>CTA Button Text</label>
          <input
            name="finalCtaButtonText"
            className="form-control mb-2"
            value={formData.finalCtaButtonText}
            onChange={handleChange}
          />

          <label>CTA Button Link</label>
          <input
            name="finalCtaButtonLink"
            className="form-control mb-4"
            value={formData.finalCtaButtonLink}
            onChange={handleChange}
          />

          <button type="submit" className="btn btn-success me-2" disabled={saving}>
            {saving ? "Saving..." : editId ? "Update" : "Save"}
          </button>

          <button type="button" className="btn btn-secondary" onClick={resetForm}>
            Cancel
          </button>
        </form>
      )}
    </div>
  );
}

export default OnlineYogaForm;
